import React, { useEffect, useState } from 'react';
import { Play, Plus, Check, Info, Star, Volume2, VolumeX } from 'lucide-react';
import { imgUrl, getTrailerKey } from '../api/tmdb';
import { useApp } from '../context/AppContext';
import { useDetail } from '../context/DetailContext';
import { useSubscription } from '../context/SubscriptionContext';
import useYouTubeProgress from '../hooks/useYouTubeProgress';
import Paywall from './Paywall';

const Hero = ({ item }) => {
  const { open } = useDetail();
  const { isInList, toggleMyList } = useApp();
  const { isSubscribed } = useSubscription();
  const [trailerKey, setTrailerKey] = useState(null);
  const [muted, setMuted] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const [paywall, setPaywall] = useState(false);

  useEffect(() => {
    let t;
    setShowVideo(false);
    setTrailerKey(null);
    if (item) {
      const mt = item.media_type || (item.first_air_date ? 'tv' : 'movie');
      getTrailerKey(mt, item.id).then((k) => {
        if (k) {
          setTrailerKey(k);
          t = setTimeout(() => setShowVideo(true), 1500);
        }
      });
    }
    return () => clearTimeout(t);
  }, [item?.id]);

  const title = item?.title || item?.name;
  const overview = item?.overview;
  const year = (item?.release_date || item?.first_air_date || '').slice(0, 4);
  const inList = isInList(item);
  const mediaType = item?.media_type || (item?.first_air_date ? 'tv' : 'movie');

  // Track hero trailer progress (muted or not, still counts views)
  useYouTubeProgress(
    trailerKey && showVideo && item ? `hero-yt-${item.id}` : null,
    item ? {
      tmdb_id: item.id,
      media_type: mediaType,
      title,
      poster_path: item.poster_path,
      backdrop_path: item.backdrop_path,
      youtube_id: trailerKey,
    } : null,
    !!(trailerKey && showVideo && item),
  );

  if (!item) {
    return <div className="h-[80vh] bg-gradient-to-b from-white/5 to-transparent animate-pulse" />;
  }

  return (
    <section className="relative h-[88vh] min-h-[560px] w-full overflow-hidden">
      {/* Backdrop image */}
      <img
        src={imgUrl(item.backdrop_path || item.poster_path, 'original')}
        alt={title}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${showVideo ? 'opacity-0' : 'opacity-100'}`}
      />

      {/* YouTube trailer */}
      {trailerKey && showVideo && (
        <div className="absolute inset-0 pointer-events-none">
          <iframe
            id={`hero-yt-${item.id}`}
            title="hero-trailer"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140vw] h-[140vh]"
            src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1&mute=${muted ? 1 : 0}&controls=0&loop=1&playlist=${trailerKey}&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&enablejsapi=1`}
            frameBorder="0"
            allow="autoplay; encrypted-media"
          />
        </div>
      )}

      {/* Gradients */}
      <div className="absolute inset-0 hero-left-fade" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 hero-bottom-fade" />

      {/* Content */}
      <div className="relative z-10 h-full flex items-end pb-24 pl-10 pr-10 md:pl-16">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2 py-1 rounded-md text-[10px] font-bold tracking-widest bg-white/15 text-white border border-white/25">TRAILER</span>
            {item.vote_average ? (
              <span className="flex items-center gap-1 text-white/90 text-sm"><Star size={14} className="text-amber-400 fill-amber-400" /> {item.vote_average.toFixed(1)}</span>
            ) : null}
            {year && <span className="text-white/70 text-sm">{year}</span>}
          </div>
          <h1 className="font-display text-5xl md:text-7xl text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.8)] leading-[0.95]">
            {title}
          </h1>
          <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed line-clamp-3 max-w-xl">
            {overview}
          </p>

          <div className="mt-7 flex items-center gap-3 flex-wrap">
            <button
              onClick={() => open(item)}
              className="tv-focusable inline-flex items-center gap-2 px-7 py-3 rounded-lg bg-white text-black font-semibold hover:bg-white/90"
            >
              <Play size={20} className="fill-black" /> Watch Trailer
            </button>
            <button
              onClick={() => open(item)}
              className="tv-focusable inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white/15 text-white font-semibold hover:bg-white/25 backdrop-blur"
            >
              <Info size={18} /> More info
            </button>
            <button
              onClick={() => toggleMyList(item)}
              className="tv-focusable inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white/10 text-white hover:bg-white/20 backdrop-blur"
            >
              {inList ? <Check size={18} /> : <Plus size={18} />} {inList ? 'My List' : 'Add to List'}
            </button>
          </div>
        </div>
      </div>

      {/* Mute toggle */}
      {trailerKey && (
        <button
          onClick={() => setMuted((m) => !m)}
          className="absolute right-8 bottom-28 z-10 w-12 h-12 rounded-full border border-white/30 bg-black/50 hover:bg-black/80 flex items-center justify-center text-white tv-focusable"
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      )}
      <Paywall open={paywall} onClose={() => setPaywall(false)} />
    </section>
  );
};

export default Hero;
